import { useFormContext, useWatch } from "react-hook-form";
import { SetpointFormType} from "../AlarmSetpointEditorUtils";
import { Box, Card, CardContent,  CardHeader,  Typography  } from "@mui/material";
import { isMobile, isTablet } from "react-device-detect";
import AlarmSetpointSummaryUtils from "./AlarmSetpointSummaryUtils";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useSetpointReferenceData } from "../../../queries/alarm-setpoint-admin/UseSetpointReferenceData";
import { useGroup } from "../../../queries/groups/UseGroup";
import { useTranslation } from "react-i18next";
import { UsageSummaryList } from "./UsageSummaryList";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import NoData from "../../no-data/NoData";
import { RecipientSummaryList } from "./RecipientSummaryList";
import { NotificationListSummaryList } from "./NotificationListSummaryList";
import { AlarmSchemeListItem } from "../../alarm-scheme-list/AlarmSchemeListItem";
import { IEditTagDTO, IEditTagSearchResultDeviceDTO } from "../../../services/setpoint-administration-service/Models";
import Enumerable from "linq";

export type AlarmSetpointSummaryProps = {
     groupID: number,
};

export default function AlarmSetpointSummary({
    groupID
}: AlarmSetpointSummaryProps) {

    const [userState] = useUserContext();
    const { t } = useTranslation();
    const {
        //getValues,
        //watch,
        //trigger,
        control,
        //reset,
        //setValue
    } = useFormContext<SetpointFormType>();    

    const setpoint = useWatch({ control: control }) as SetpointFormType;    

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });


    const organizationID = setpoint.setPointID > 0 ? setpoint.organizationID : group?.organizationID as number;

    const {data: referenceData /*status: referenceStatus */} = useQueryNotifications(useSetpointReferenceData)({
        groupID ,
        organizationID: organizationID,
        enabled: groupID !== undefined
    });

    const isPhone = isMobile && !isTablet;

    const {alarmText, clearText} = AlarmSetpointSummaryUtils.logicSummary(setpoint, referenceData?.unitsOfMeasure ?? []);
    const messagingText = AlarmSetpointSummaryUtils.actionSummary(setpoint);
    // const devices = setpoint.devices.filter(o => o.tags?.filter(v => v.selected).length > 0);
    // const wells = setpoint.wells.filter(o => o.tags?.filter(v => v.selected).length > 0);
    // const tanks = setpoint.tanks.filter(o => o.tags?.filter(v => v.selected).length > 0);
    // const tankBatteries = setpoint.tankBatteries.filter(o => o.tags?.filter(v => v.selected).length > 0);
    const deviceTags = setpoint.deviceTags;
    const wellTags = setpoint.wellTags;
    const tankTags = setpoint.tankTags;
    const tankBatteryTags = setpoint.tankBatteryTags;

    const devices = Enumerable
        .from(deviceTags)
        .groupBy<IEditTagDTO, IEditTagDTO, IEditTagSearchResultDeviceDTO, string>(
            k => k,
            e => e,
            (key, group) => {
                return {
                    id: key.tagObjectID,
                    name: key.tagObjectName,
                    objectTypeID: key.tagObjectTypeID,
                    tags: group.select(t => ({
                        id: t.tagID,
                        name: t.tagName,
                        selected: t.selected
                    })).toArray()
                };
            },
            t => `${t.tagObjectTypeID}:${t.tagObjectID}`
        )
        .toArray();

    const wells = Enumerable
        .from(wellTags)
        .groupBy<IEditTagDTO, IEditTagDTO, IEditTagSearchResultDeviceDTO, string>(
            k => k,
            e => e,
            (key, group) => {
                return {
                    id: key.tagObjectID,
                    name: key.tagObjectName,
                    objectTypeID: key.tagObjectTypeID,
                    tags: group.select(t => ({
                        id: t.tagID,
                        name: t.tagName,
                        selected: t.selected
                    })).toArray()
                };
            },
            t => `${t.tagObjectTypeID}:${t.tagObjectID}`
        )
        .toArray();

    const tanks = Enumerable
        .from(tankTags)
        .groupBy<IEditTagDTO, IEditTagDTO, IEditTagSearchResultDeviceDTO, string>(
            k => k,
            e => e,
            (key, group) => {
                return {
                    id: key.tagObjectID,
                    name: key.tagObjectName,
                    objectTypeID: key.tagObjectTypeID,
                    tags: group.select(t => ({
                        id: t.tagID,
                        name: t.tagName,
                        selected: t.selected
                    })).toArray()
                };
            },
            t => `${t.tagObjectTypeID}:${t.tagObjectID}`
        )
        .toArray();

    const tankBatteries = Enumerable
        .from(tankBatteryTags)
        .groupBy<IEditTagDTO, IEditTagDTO, IEditTagSearchResultDeviceDTO, string>(
            k => k,
            e => e,
            (key, group) => {
                return {
                    id: key.tagObjectID,
                    name: key.tagObjectName,
                    objectTypeID: key.tagObjectTypeID,
                    tags: group.select(t => ({
                        id: t.tagID,
                        name: t.tagName,
                        selected: t.selected
                    })).toArray()
                };
            },
            t => `${t.tagObjectTypeID}:${t.tagObjectID}`
        )
        .toArray();

    const usageCount = devices.length + wells.length + tanks.length + tankBatteries.length;

    const alarmScheme = setpoint.alarmSchemes.find(o => o.selected);
    const recipients = setpoint.userRecipients.filter(o => o.recipients.find(v => v.selected) !== undefined);
    const notificationLists = setpoint.notificationLists.filter(o => o.selected);

    const notificationCount = (alarmScheme ? 1 : 0) + recipients.length + notificationLists.length;



    const rectangle = (color: string) => <Box component="span" sx={{ display: 'inline-block', 
                    verticalAlign: 'middle',
                    marginLeft: 1,
                    bgcolor: color, 
                    width: 16, 
                    height: 16 }} />;


    function renderAction(text: string, color: string) {
        return (
            <Typography variant='body1' component='div' noWrap={false}  sx={{
                '& + &': {marginTop: 1}
            }}>
                {text}
                <Box component='span' sx={{
                    color: color
                }}>{color.toLowerCase()}</Box>
                {rectangle(color)}
            </Typography>
        );
    }

   return (
       // </Stack>
       // </Paper>        
       <Box sx={{
               overflow: "hidden",
               overflowY: 'auto',
               padding: 1,
               height: '100%',
               backgroundColor: "custom.background.cardContainer",
           }}>
           <Card variant="outlined">
               <CardHeader
                   sx={{
                       padding: 1
                   }}
                   title='Alarm Logic'
                   slotProps={{
                       title: {
                           variant: 'h6'
                       }
                   }}
               >
               </CardHeader>
               {/* <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>
                   <Button
                       sx={{
                           color: "text.primary"
                       }}
                       variant="text"
                       onClick={() => toggleExpanded()}
                       style={{ marginLeft: "auto" }}
                       endIcon={<ExpandMore sx={{
                           transform: !expanded ? "rotate(0deg)" : "rotate(180deg)",
                           transition: theme.transitions.create('transform', {
                               duration: theme.transitions.duration.shortest,
                           })
                       }} />}
                   >
                       {t('setpointAdmin.usage', {count: linkedTags.length})}
                   </Button>
               </CardActions> */}


               <CardContent sx={{
                   padding: isPhone ? 
                       (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important` : 
                       undefined,
               }}>

                   <Typography variant='body1' noWrap={false}>
                       {alarmText}
                   </Typography>

                   <Typography variant='body1' noWrap={false} sx={{
                       marginTop: 1
                   }}>
                       {clearText}
                   </Typography>
               </CardContent>

           </Card>
           <Card variant="outlined" sx={{
               marginTop: 1,
           }}>
               <CardHeader
                   sx={{
                       padding: 1
                   }}
                   title='Alarm Actions'
                   slotProps={{
                       title: {
                           variant: 'h6'
                       }
                   }}
               >
               </CardHeader>

               <CardContent sx={{
                   padding: isPhone ? 
                       (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important` : 
                       undefined,
               }}>
                   {renderAction(t('setpointAdmin.summary.tagsInAlarm'), setpoint.alarmColor)}
                   {renderAction(t('setpointAdmin.summary.ackTags'), setpoint.acknowledgeColor)}

                   <Typography
                       variant='body1'
                       noWrap={false}
                       sx={{
                           display: 'flex',
                           marginTop: 1
                       }}>
                       {messagingText}
                   </Typography>
               </CardContent>

           </Card>
           <Card variant="outlined" sx={{
               marginTop: 1,
           }}>
               <CardHeader
                   sx={{
                       padding: 1
                   }}
                   title='Usage'
                   slotProps={{
                       title: {
                           variant: 'h6'
                       }
                   }}
               >
               </CardHeader>

               <CardContent sx={{
                   padding: isPhone ? 
                       (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important` : 
                       undefined,
               }}>

                   {usageCount > 0 ?
                   <>
                       <Typography
                           variant='body1'
                           noWrap={false}
                           sx={{
                               display: 'flex',
                               marginTop: 1
                           }}>
                           {t('setpointAdmin.summary.usageSummaryTitle')}
                       </Typography>

                       {devices.length > 0 && 
                           <UsageSummaryList name='devices' title={getObjectTypeName(eLynxObjectTypeEnum.Device, true)} items={devices} />
                       }

                       {wells.length > 0 && 
                           <UsageSummaryList name='wells' title={getObjectTypeName(eLynxObjectTypeEnum.Well, true)} items={wells} />
                       }

                       {tanks.length > 0 && 
                           <UsageSummaryList name='tanks' title={getObjectTypeName(eLynxObjectTypeEnum.Tank, true)} items={tanks} />
                       }

                       {tankBatteries.length > 0 && 
                           <UsageSummaryList name='tankbatteries' title={getObjectTypeName(eLynxObjectTypeEnum.TankBattery, true)} items={tankBatteries} />
                       }

                   </>
                   :
                   <Box sx={{
                       padding: 1
                   }}>
                       <NoData title='' text={t('setpointAdmin.summary.noUsageObjects')} />
                   </Box>

                   }


               </CardContent>

           </Card>
           <Card variant="outlined" sx={{
               marginTop: 1,
           }}>
               <CardHeader
                   sx={{
                       padding: 1
                   }}
                   title='Notifications'
                   slotProps={{
                       title: {
                           variant: 'h6'
                       }
                   }}
               >
               </CardHeader>

               <CardContent sx={{
                   padding: isPhone ? 
                       (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important` : 
                       undefined,
               }}>

                   {notificationCount > 0 ?
                   <>
                       <Typography
                           variant='body1'
                           noWrap={false}
                           sx={{
                               display: 'flex',
                               marginTop: 1
                           }}>
                           {t(alarmScheme ? 'setpointAdmin.summary.alarmSchemeSummaryTitle' : 'setpointAdmin.summary.notificationSummaryTitle')}
                       </Typography>

                       {alarmScheme ?
                           <AlarmSchemeListItem item={alarmScheme} 
                               allowEdit={false} 
                               enableSelection={false}
                               sx={{
                                   border: 'none'
                               }} />
                       :
                       <>
                       {
                           recipients.length > 0 && 
                           <RecipientSummaryList title={getObjectTypeName(eLynxObjectTypeEnum.Recipient, true)} items={recipients} />
                       }

                       {
                           notificationLists.length > 0 && 
                           <NotificationListSummaryList title={getObjectTypeName(eLynxObjectTypeEnum.NotificationList, true)} items={notificationLists} />
                       }
                       </>
                       }

                   </>
                   :
                   <Box sx={{
                       padding: 1
                   }}>
                       <NoData title='' text={t('setpointAdmin.summary.noNotifications')} />
                   </Box>

                   }


               </CardContent>

           </Card>
       </Box>
   );
  }
